import { Injectable, OnDestroy } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { switchMap, filter } from 'rxjs/operators';
import { DataService } from './data.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class HealthMonitorService implements OnDestroy {
  private healthCheckSubscription?: Subscription;
  private readonly POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds

  constructor(
    private dataService: DataService,
    private authService: AuthService
  ) {
    this.initializeHealthCheck();
  }

  ngOnDestroy(): void {
    this.stopHealthCheck();
  }

  /**
   * Initialize health check polling when user is logged in
   */
  private initializeHealthCheck(): void {
    // Monitor authentication state
    this.authService.currentUser$.subscribe(user => {
      if (user) {
        this.startHealthCheck();
      } else {
        this.stopHealthCheck();
      }
    });
  }

  /**
   * Start health check polling
   */
  private startHealthCheck(): void {
    // Stop any existing subscription first
    this.stopHealthCheck();

    // Perform immediate health check
    this.performHealthCheck();

    // Set up periodic polling
    this.healthCheckSubscription = interval(this.POLL_INTERVAL)
      .pipe(
        filter(() => this.authService.isAuthenticated()),
        switchMap(() => this.dataService.healthCheck())
      )
      .subscribe({
        next: (response) => {
          if (response.message === 'Success') {
            console.log('Health check successful:', response.response);
          } else {
            console.warn('Health check returned non-success status:', response);
          }
        },
        error: (error) => {
          console.error('Health check failed:', error);
        }
      });
  }

  /**
   * Stop health check polling
   */
  private stopHealthCheck(): void {
    if (this.healthCheckSubscription) {
      this.healthCheckSubscription.unsubscribe();
      this.healthCheckSubscription = undefined;
    }
  }

  /**
   * Perform a manual health check
   */
  private performHealthCheck(): void {
    if (this.authService.isAuthenticated()) {
      this.dataService.healthCheck().subscribe({
        next: (response) => {
          if (response.message === 'Success') {
            console.log('Initial health check successful:', response.response);
          } else {
            console.warn('Initial health check returned non-success status:', response);
          }
        },
        error: (error) => {
          console.error('Initial health check failed:', error);
        }
      });
    }
  }

  /**
   * Manually trigger a health check (useful for testing or on-demand checks)
   */
  public checkHealth(): void {
    this.performHealthCheck();
  }
}
